import { signal } from '@preact/signals-react';
import type { Signal } from '@preact/signals-react';
import type {
    InfiniteQueryObserver,
    InfiniteQueryObserverResult,
    QueryKey,
    QueryObserver,
    QueryObserverResult,
} from '@tanstack/react-query';

export interface QueryObserverSignalDisposable<
    TQueryFnData = unknown,
    TError = unknown,
    TData = TQueryFnData,
    TQueryData = TQueryFnData,
    TQueryKey extends QueryKey = QueryKey,
> extends Disposable {
    querySignal: Signal<QueryObserverResult<TData, TError>>;
    queryObserver: QueryObserver<TQueryFnData, TError, TData, TQueryData, TQueryKey>;
}

export interface InfiniteQueryObserverSignalDisposable<
    TQueryFnData = unknown,
    TError = unknown,
    TData = TQueryFnData,
    TQueryKey extends QueryKey = QueryKey,
> extends Disposable {
    querySignal: Signal<InfiniteQueryObserverResult<TData, TError>>;
    queryObserver: InfiniteQueryObserver<TQueryFnData, TError, TData, TQueryKey>;
}

/**
 * Converts a QueryObserver to a Preact signal that updates when the query data changes.
 * This allows QueryObserver results to be used reactively in signal-based components.
 *
 * @param queryObserver - The QueryObserver instance to subscribe to
 * @returns A Disposable object containing the signal, cleanup method, and QueryObserver instance.
 */
export function queryObserverSignal<
    TQueryFnData = unknown,
    TError = unknown,
    TData = TQueryFnData,
    TQueryData = TQueryFnData,
    TQueryKey extends QueryKey = QueryKey,
>(
    queryObserver: QueryObserver<TQueryFnData, TError, TData, TQueryData, TQueryKey>,
): QueryObserverSignalDisposable<TQueryFnData, TError, TData, TQueryData, TQueryKey> {
    // Create initial signal with the current query state
    const querySignal = signal<QueryObserverResult<TData, TError>>(queryObserver.getCurrentResult());

    // Subscribe to the observer to update the signal when the query state changes
    const unsubscribe = queryObserver.subscribe((result) => {
        querySignal.value = result;
    });

    return {
        queryObserver,
        querySignal,
        [Symbol.dispose]() {
            unsubscribe();
        },
    };
}

/**
 * Infinite query version of @method queryObserverSignal
 */
export function infiniteQueryObserverSignal<
    TQueryFnData = unknown,
    TError = unknown,
    TData = TQueryFnData,
    TQueryKey extends QueryKey = QueryKey,
>(
    queryObserver: InfiniteQueryObserver<TQueryFnData, TError, TData, TQueryKey>,
): InfiniteQueryObserverSignalDisposable<TQueryFnData, TError, TData, TQueryKey> {
    const querySignal = signal<InfiniteQueryObserverResult<TData, TError>>(queryObserver.getCurrentResult());

    const unsubscribe = queryObserver.subscribe((result) => {
        querySignal.value = result;
    });

    return {
        queryObserver,
        querySignal,
        [Symbol.dispose]() {
            unsubscribe();
        },
    };
}
